import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatIndentIncreaseRoundedIcon from "@mui/icons-material/FormatIndentIncreaseRounded";
import FormatIndentDecreaseRoundedIcon from "@mui/icons-material/FormatIndentDecreaseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import dayjs from "dayjs";
import { replacePlaceholders } from "helpers/assignmentPlaceholders";

const htmlToPlainText = (html) => {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  container.querySelectorAll("br").forEach((node) => {
    node.replaceWith("\n");
  });

  container.querySelectorAll("p, div, li, tr").forEach((node) => {
    if (node.textContent?.trim()) {
      node.appendChild(document.createTextNode("\n"));
    }
  });

  return (container.innerText || container.textContent || "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const copyElementContents = (element) => {
  if (!element) return false;

  const selection = window.getSelection();
  if (!selection) return false;

  const previousRanges = [];
  for (let i = 0; i < selection.rangeCount; i += 1) {
    previousRanges.push(selection.getRangeAt(i).cloneRange());
  }

  const range = document.createRange();
  range.selectNodeContents(element);

  selection.removeAllRanges();
  selection.addRange(range);

  let didCopy = false;
  try {
    didCopy = document.execCommand("copy");
  } finally {
    selection.removeAllRanges();
    previousRanges.forEach((savedRange) => selection.addRange(savedRange));
  }

  return didCopy;
};

const InterpretationSendDetails = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar, openDialog, closeDialog } =
    useStateContext();
  const editorRef = useRef(null);

  const assignmentData = useMemo(
    () => overlayData?.Assignment || {},
    [overlayData?.Assignment],
  );

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [modifiers, setModifiers] = useState([]);

  const linguistEmail =
    assignmentData?.Linguist?.Email ||
    assignmentData?.Linguist?.EmailAddress ||
    assignmentData?.Linguist?.PersonalEmail ||
    "";

  const loadTemplateData = useCallback(
    (template) => {
      const replacedSubject = replacePlaceholders(
        template.Subject,
        assignmentData,
      );
      const replacedBody = replacePlaceholders(template.Body, assignmentData);

      setSubject(replacedSubject);
      setBodyHtml(replacedBody);

      if (editorRef.current) {
        editorRef.current.innerHTML = replacedBody;
      }
    },
    [assignmentData],
  );

  // Latest-value refs for the template fetch effect below. That effect must
  // stay keyed to the ASSIGNMENT IDENTITY only — these callback/context
  // identities change on unrelated re-renders (e.g. every snackbar), which
  // used to re-run the fetch, blink the overlay, and reset a manually
  // picked template back to the auto-selected default.
  const loadTemplateDataRef = useRef(loadTemplateData);
  loadTemplateDataRef.current = loadTemplateData;
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;
  const themeRef = useRef(theme);
  themeRef.current = theme;

  // Tracks templates the user manually picked, per assignment id. These
  // overrides survive refetches — auto-selection only applies as the
  // initial default (same pattern as the Reminders overlay fix).
  const userTemplateOverridesRef = useRef({});

  // Fetch email templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const [templatesResponse, modifiersResponse] = await Promise.all([
          api.get("/communicationTemplates?channel=Email&type=Template"),
          api.get("/communicationTemplates?channel=Email&type=Modifier"),
        ]);

        const templates = templatesResponse.data || [];
        setEmailTemplates(templates);
        setModifiers(modifiersResponse.data || []);

        // Single source of truth for the dropdown: only Email "Template"
        // records. The server now filters by channel/type too, but keeping
        // this guard ensures the dropdown and the auto-selection can never
        // disagree (an out-of-range MUI select was happening before).
        const selectableTemplates = templates.filter(
          (t) => t.Type === "Template" || !t.Type,
        );

        // Values from the assignment used to evaluate match rules.
        const assignmentValues = {
          Client: assignmentData?.Client?.Account || "",
          Requestor: assignmentData?.Requestor?.Name || "",
          Linguist: assignmentData?.Linguist?.Name || "",
          InterpretationType:
            assignmentData?.ServiceSubtype?.Name ||
            assignmentData?.InterpretationType ||
            "",
          Language:
            assignmentData?.ServiceTargetLanguages?.[0]?.Language?.Name || "",
          Mode: assignmentData?.Mode || "",
          ServiceCategory: assignmentData?.ServiceCategory || "",
          InterpretationStatus: assignmentData?.InterpretationStatus || "",
          Name: assignmentData?.Name || "",
          Details: assignmentData?.Details || "",
          Address: assignmentData?.Address || "",
          ProjectNumber: assignmentData?.ProjectNumber || "",
        };

        // Specificity of each rule field: lower = higher priority when
        // multiple rules match (a client-specific template beats a generic
        // type template, which beats a name-contains template).
        const RULE_FIELD_SPECIFICITY = {
          Client: 10,
          Linguist: 12,
          Requestor: 14,
          InterpretationType: 20,
          Language: 30,
          Mode: 40,
          ServiceCategory: 50,
          Name: 60,
          ProjectNumber: 65,
          Details: 70,
          Address: 80,
          InterpretationStatus: 90,
        };

        const matchesRule = (rule, actualValue) => {
          const ruleValue = String(rule?.Value || "").trim();
          const actual = String(actualValue || "").trim();
          if (!ruleValue || !actual) return false;
          if (rule?.MatchType === "contains") {
            return actual.toLowerCase().includes(ruleValue.toLowerCase());
          }
          return actual.toLowerCase() === ruleValue.toLowerCase();
        };

        // 1) Rule-based auto-selection: the template whose rule matches with
        //    the highest priority wins.
        let selectedTemplate = null;
        let bestRuleRank = null;

        selectableTemplates.forEach((template) => {
          (template.MatchRules || []).forEach((rule) => {
            const actualValue = assignmentValues[rule.Field];
            if (actualValue === undefined || !matchesRule(rule, actualValue)) {
              return;
            }
            const specificity = RULE_FIELD_SPECIFICITY[rule.Field] ?? 100;
            const rank = specificity * 1000 + (Number(rule.Priority) || 0);
            if (bestRuleRank === null || rank < bestRuleRank) {
              bestRuleRank = rank;
              selectedTemplate = template;
            }
          });
        });

        // 2) Final fallback: first selectable template.
        if (!selectedTemplate) {
          selectedTemplate = selectableTemplates[0];
        }

        // If the user manually picked a template for this assignment, keep
        // their choice — auto-selection only applies as the initial default
        // (mirrors the Reminders overlay fix). Do NOT reload the template
        // data in that case: the user's subject/body (and any manual edits)
        // must survive the refetch.
        const overrideKey =
          assignmentData?.id ?? overlayData?.ServiceID ?? "default";
        const userOverrideId = userTemplateOverridesRef.current[overrideKey];
        const overrideTemplate = userOverrideId
          ? selectableTemplates.find((t) => t.id === userOverrideId)
          : null;

        if (overrideTemplate) {
          setSelectedTemplateId(overrideTemplate.id);
        } else if (selectedTemplate) {
          setSelectedTemplateId(selectedTemplate.id);
          loadTemplateDataRef.current(selectedTemplate);
        }
      } catch (err) {
        console.error("Error fetching email templates:", err);
        showSnackbarRef.current(
          "Error loading email templates",
          themeRef.current,
          "error",
        );
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
    // Keyed to the assignment identity ONLY: showSnackbar / theme / the
    // loadTemplateData callback identity all change on unrelated provider
    // re-renders (e.g. every snackbar), which re-ran this effect, blinked
    // the overlay (loadingTemplates flash), and reset a manually picked
    // template back to the auto-selected default. Refs carry the latest
    // values instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignmentData?.id,
    assignmentData?.Client?.Account,
    assignmentData?.ServiceSubtype?.Name,
    assignmentData?.Name,
  ]);

  const handleTemplateChange = (event) => {
    const templateId = event.target.value;
    // Record the user's explicit choice so re-fetches/re-renders keep it.
    const overrideKey =
      assignmentData?.id ?? overlayData?.ServiceID ?? "default";
    userTemplateOverridesRef.current[overrideKey] = templateId;
    setSelectedTemplateId(templateId);

    const selectedTemplate = emailTemplates.find((t) => t.id === templateId);
    if (selectedTemplate) {
      loadTemplateData(selectedTemplate);
    }
  };

  const handleBodyInput = (e) => {
    setBodyHtml(e.currentTarget.innerHTML);
  };

  const [savedRange, setSavedRange] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);

  const handleModifierMouseDown = (e) => {
    e.preventDefault();
    // Save the current selection before the click
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0));
    }
  };

  const insertModifier = (modifier) => {
    const el = editorRef.current;
    if (!el) return;

    // Focus the editor
    el.focus();

    // Restore the saved selection if available
    if (savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange);
      setSavedRange(null);
    }

    // Insert at cursor position using execCommand
    document.execCommand("insertHTML", false, modifier.Body);

    // Re-run placeholder replacement on the updated content
    const updatedContent = el.innerHTML;
    const replacedContent = replacePlaceholders(updatedContent, assignmentData);
    el.innerHTML = replacedContent;

    setBodyHtml(el.innerHTML);
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleOpenLinkDialog = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0));
    }

    openDialog({
      theme: theme,
      title: "Insert Hyperlink",
      message: "",
      customContent: (
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateLink();
              }
            }}
            autoFocus
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Cancel",
      primaryAction: "Insert Link",
      tertiaryOnClick: null,
      secondaryOnClick: () => {
        closeDialog();
        setLinkUrl("");
        setSavedSelection(null);
      },
      primaryOnClick: () => {
        handleCreateLink();
      },
    });
  };

  const handleCloseLinkDialog = () => {
    closeDialog();
    setLinkUrl("");
    setSavedSelection(null);
  };

  const handleCreateLink = () => {
    if (!linkUrl.trim()) {
      showSnackbar("Please enter a URL", theme, "error");
      return;
    }

    const el = editorRef.current;
    if (!el) return;

    el.focus();

    // Restore the saved selection
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }

    // Create link with the provided URL
    let url = linkUrl.trim();
    // Add protocol if not present
    if (!url.match(/^https?:\/\//)) {
      url = "https://" + url;
    }

    document.execCommand("createLink", false, url);
    setBodyHtml(el.innerHTML);
    handleCloseLinkDialog();
  };

  const closeOverlay = () => setOverlayName("");

  const copyToClipboard = async ({ plainText, html, successMessage }) => {
    try {
      if (navigator.clipboard?.write && window.ClipboardItem && html) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = plainText;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      showSnackbar(successMessage, theme, "success");
    } catch (error) {
      console.error("Error copying content:", error);
      showSnackbar("Unable to copy content", theme, "error");
    }
  };

  const handleCopySubject = async () => {
    const plainText = (subject || "").trim();

    if (!plainText) {
      showSnackbar("Nothing to copy", theme, "warning");
      return;
    }

    await copyToClipboard({
      plainText,
      successMessage: "Subject copied",
    });
  };

  const handleCopyBody = async () => {
    const plainText = htmlToPlainText(bodyHtml);
    const html = bodyHtml?.trim();

    if (!plainText) {
      showSnackbar("Nothing to copy", theme, "warning");
      return;
    }

    if (copyElementContents(editorRef.current)) {
      showSnackbar("Body copied", theme, "success");
      return;
    }

    await copyToClipboard({
      plainText,
      html,
      successMessage: "Body copied",
    });
  };

  const handleCopyLinguistEmail = async () => {
    const plainText = (linguistEmail || "").trim();

    if (!plainText) {
      showSnackbar("No linguist email found", theme, "warning");
      return;
    }

    await copyToClipboard({
      plainText,
      successMessage: "Linguist email copied",
    });
  };

  const handleCopyEmailPackage = async () => {
    // Check if there are any unhandled manual replacements
    if (bodyHtml.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (bodyHtml.match(/NO_DATA_REPLACE_MANUALLY/g) || []).length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't send until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    const packageParts = [];

    if (linguistEmail.trim()) {
      packageParts.push(`To: ${linguistEmail.trim()}`);
    }

    packageParts.push(`Subject: ${(subject || "").trim()}`);
    packageParts.push("");
    packageParts.push(htmlToPlainText(bodyHtml));

    await copyToClipboard({
      plainText: packageParts.join("\n"),
      successMessage: "Email package copied",
    });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          maxHeight: "90%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Send Details
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Copy package" placement="top">
                  <IconButton
                    onClick={handleCopyEmailPackage}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    <ContentCopyRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<ContentCopyRoundedIcon />}
                  onClick={handleCopyEmailPackage}
                  sx={{ borderRadius: "12px" }}
                >
                  Copy package
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Email Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Copy the recipient, subject, and body into your mail client. Manual replacements must be resolved before the package can be copied.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ maxLength: 255 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy" placement="top">
                        <IconButton edge="end" size="small" onClick={handleCopySubject}>
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Linguist Email"
                value={linguistEmail}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy" placement="top">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={handleCopyLinguistEmail}
                          disabled={!linguistEmail}
                        >
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Body
                    </Typography>

                    {/* Formatting Toolbar */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Tooltip title="Bold">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("bold")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatBoldRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Italic">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("italic")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatItalicRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Underline">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("underline")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatUnderlinedRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Divider orientation="vertical" sx={{ my: 0.5 }} />
                      <Tooltip title="Bullet List">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("insertUnorderedList")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatListBulletedRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Numbered List">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("insertOrderedList")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatListNumberedRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Divider orientation="vertical" sx={{ my: 0.5 }} />
                      <Tooltip title="Indent Right">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("indent")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatIndentIncreaseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Indent Left">
                        <IconButton
                          size="small"
                          onClick={() => applyFormat("outdent")}
                          sx={{ borderRadius: "8px" }}
                        >
                          <FormatIndentDecreaseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Divider orientation="vertical" sx={{ my: 0.5 }} />
                      <Tooltip title="Insert Hyperlink">
                        <IconButton
                          size="small"
                          onClick={handleOpenLinkDialog}
                          sx={{ borderRadius: "8px" }}
                        >
                          <LinkRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ position: "relative" }}>
                    <Tooltip title="Copy" placement="top">
                      <IconButton
                        size="small"
                        onClick={handleCopyBody}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          zIndex: 1,
                          border: `1px solid ${theme.palette.primary.border2}`,
                          "&:hover": {
                            bgcolor: theme.palette.primary.light,
                          },
                        }}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Box
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleBodyInput}
                      sx={{
                        boxSizing: "border-box",
                        height: 340,
                        maxHeight: 340,
                        p: 1.5,
                        pt: 6,
                        pr: 6,
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.primary.border2}`,
                        bgcolor: theme.palette.background.default,
                        outline: "none",
                        "&:focus": {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
                        },
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        "& > *:first-of-type": {
                          mt: "0 !important",
                        },
                        "& p, & div, & ul, & ol": {
                          mt: 0,
                          mb: 1,
                        },
                        "& p:last-child, & div:last-child, & ul:last-child, & ol:last-child": {
                          mb: 0,
                        },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Template
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      value={selectedTemplateId || ""}
                      onChange={handleTemplateChange}
                      label="Template"
                      size="small"
                      disabled={loadingTemplates}
                    >
                      {emailTemplates
                        .filter((t) => t.Type === "Template" || !t.Type)
                        .map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.Name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Modifiers
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      bgcolor: theme.palette.background.default,
                      height: 265,
                      maxHeight: 265,
                      overflowY: "auto",
                    }}
                  >
                    {modifiers.length > 0 ? (
                      modifiers
                        .filter((t) => t.Type === "Modifier")
                        .map((modifier) => (
                          <Box
                            key={modifier.id}
                            onMouseDown={handleModifierMouseDown}
                            onClick={() => insertModifier(modifier)}
                            sx={{
                              border: `1px solid ${theme.palette.primary.border2}`,
                              borderRadius: "10px",
                              p: 1,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: theme.palette.primary.main,
                                bgcolor: `${theme.palette.primary.main}0a`,
                              },
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {modifier.Name}
                            </Typography>
                          </Box>
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No modifiers available
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default InterpretationSendDetails;
